/**
 * Recovery-only: re-establish a business entity change that was pruned from
 * the cloud sync log.
 *
 * CONTEXT
 * --------
 * The buggy `sync_prune_compacted` (migration 20261010000000) deleted
 * `sync_changes` rows by cursor range — including the business entity change
 * if it happened to sit below the pruning floor. The fix migration
 * (20261027000001) prevents future pruning of latest-version rows, but
 * businesses already affected have no business row in
 * `sync_fetch_snapshot`, causing `restoreCloudBusiness` and
 * `SyncEngine.recover()` to fail with "The synced business snapshot has no
 * business row."
 *
 * THIS FILE IS RECOVERY-ONLY
 * ---------------------------
 * It is NOT called during normal sync. `SyncEngine.run()` never invokes it.
 * It is called explicitly by `restoreCloudBusiness` (existing-device path)
 * or by a "repair cloud sync" action in the UI. A recovery operation has
 * different safety requirements from a sync cycle — it must verify the
 * local business exists, check the server first, and only then mutate the
 * local outbox.
 *
 * WHAT IT DOES
 * -------------
 * 1. Validates the local business row exists and has its payload.
 * 2. Validates the original business outbox entry exists (changeId + payload).
 * 3. Confirms the local business version matches the outbox entry version.
 * 4. Calls `provider.checkBusinessRowExists()` to ask the server if it has
 *    the business entity in its snapshot.
 * 5. If the server HAS the business row → returns without modifying anything.
 * 6. If the server CONFIRMS it is MISSING → atomically resets the single
 *    outbox entry from `acked` to `pending`. The next SyncEngine.run()
 *    will push it; `sync_push_changes` will insert it as a new row.
 *
 * WHAT IT DOES NOT DO
 * --------------------
 * - Does not invent a new change ID. The original change_id is preserved.
 * - Does not touch other outbox entries, syncState, or conflicts.
 * - Does not push anything itself — that happens in the normal sync flow.
 * - Does not recover the business on a device that doesn't have it locally.
 */
import type { DNRDatabase } from '@data/db/database';
import type { Business, SyncOutboxEntry } from '@data/db/types';
import type { SyncProvider } from '@platform/cloud/types';

/** The deterministic change id used for a business that has never been pushed. */
function deterministicBusinessChangeId(businessId: string): string {
  return `business:${businessId}`;
}

/** The change id this device would use, matching ensureBusinessChangeEnqueued. */
function businessChangeIdFor(business: Business): string {
  return business.syncChangeId ?? deterministicBusinessChangeId(business.id);
}

export interface RecoverBusinessResult {
  /** Whether the local outbox entry was reset to pending. */
  readonly outboxReset: boolean;
  /** Whether the server already had the business row before recovery. */
  readonly serverHadBusiness: boolean;
  /** The change id of the business outbox entry. */
  readonly changeId: string;
  /**
   * When checkBusinessRowExists is not supported by this provider (or
   * the function fails), the recovery cannot proceed safely.
   */
  readonly skipped: boolean;
}

export type RecoverBusinessError =
  | { readonly code: 'LOCAL_BUSINESS_NOT_FOUND'; readonly message: string }
  | { readonly code: 'MISSING_PAYLOAD'; readonly message: string }
  | { readonly code: 'OUTBOX_ENTRY_NOT_FOUND'; readonly message: string }
  | { readonly code: 'OUTBOX_MISSING_PAYLOAD'; readonly message: string }
  | { readonly code: 'NOT_ACKED'; readonly message: string }
  | { readonly code: 'VERSION_MISMATCH'; readonly message: string }
  | { readonly code: 'PAYLOAD_MISMATCH'; readonly message: string }
  | { readonly code: 'PROVIDER_UNAVAILABLE'; readonly message: string };

/**
 * Attempt to recover a business entity that was pruned from the cloud.
 *
 * The function is safe to call repeatedly: if the server already has the
 * business row, it returns without modifying the outbox. If the server
 * confirms the business is missing, it resets the acked outbox entry to
 * pending so the next SyncEngine.run() will push it.
 *
 * This does NOT perform the push itself. The caller must run a sync cycle
 * after this function returns with `outboxReset: true`.
 */
export async function recoverBusinessEntity(
  db: DNRDatabase,
  businessId: string,
  provider: SyncProvider,
): Promise<RecoverBusinessResult | RecoverBusinessError> {
  if (!businessId) {
    return { code: 'LOCAL_BUSINESS_NOT_FOUND', message: 'A business id is required.' };
  }

  // ── 1. Validate local business exists ──────────────────────────────────
  const business = await db.businesses.get(businessId);
  if (!business) {
    return { code: 'LOCAL_BUSINESS_NOT_FOUND', message: `Business ${businessId} not found locally.` };
  }

  // ── 2. Validate local business has a payload ──────────────────────────
  // The business row must be a real entity with its core fields. A bare
  // stub (e.g., only an id) is not sufficient to rebuild a cloud row.
  if (!business.legalName || !business.stateCode || business.fyStartMonth === undefined) {
    return {
      code: 'MISSING_PAYLOAD',
      message: 'The local business row is missing required fields (legalName, stateCode, fyStartMonth).',
    };
  }

  // ── 3. Derive the change id the original push used ────────────────────
  const changeId = businessChangeIdFor(business);

  // ── 4. Validate the original outbox entry exists ──────────────────────
  const outboxEntry = await db.syncOutbox.get(changeId);
  if (!outboxEntry) {
    return {
      code: 'OUTBOX_ENTRY_NOT_FOUND',
      message: `No outbox entry found for change id ${changeId}. The original business change was not recorded locally.`,
    };
  }

  // ── 5. Validate outbox entry has its payload ──────────────────────────
  if (!outboxEntry.payload) {
    return {
      code: 'OUTBOX_MISSING_PAYLOAD',
      message: `The business outbox entry ${changeId} is missing its payload and cannot be re-pushed.`,
    };
  }

  // ── 6. Require the expected acknowledged state ────────────────────────
  // Only `acked` entries are candidates for recovery, and the status decides
  // the remedy, because the two non-acked states need opposite actions:
  //
  //   pending → a cycle already owns it; the next sync (Sync now) retries it
  //   failed  → the provider refused it for good, so NOTHING retries it until
  //             the user resets it in Settings → DNR Cloud Sync
  //
  // Telling both "it will be retried by the next sync cycle" was false for the
  // failed case and hid the only action that fixes it.
  if (outboxEntry.status !== 'acked') {
    const remedy =
      outboxEntry.status === 'failed'
        ? `It was permanently rejected, so it will not retry on its own. Use "Retry failed changes" in Settings → DNR Cloud Sync, then repair again.`
        : `It is queued, so the next sync cycle (Sync now) will retry it.`;
    return {
      code: 'NOT_ACKED',
      message: `The business outbox entry ${changeId} has status '${outboxEntry.status}', not 'acked'. ${remedy}`,
    };
  }

  // ── 7. Validate version matches ───────────────────────────────────────
  // The outbox entry's version must match the local business's
  // syncVersion. If they diverge, the local row has been modified since
  // the original push, and re-pushing the old outbox payload could
  // overwrite a newer version on the server.
  if (outboxEntry.version !== (business.syncVersion ?? 1)) {
    return {
      code: 'VERSION_MISMATCH',
      message: `Outbox entry version ${outboxEntry.version} does not match local business syncVersion ${business.syncVersion ?? 1}.`,
    };
  }

  // ── 8. Validate payload matches ────────────────────────────────────────
  // The outbox entry's payload must match the local business row. If the
  // local data was modified but not re-enqueued, re-pushing the stale
  // outbox payload would overwrite newer data on the cloud.
  if (!businessPayloadMatches(outboxEntry, business)) {
    return {
      code: 'PAYLOAD_MISMATCH',
      message: `The outbox entry payload does not match the local business row. The local data was modified after the original push.`,
    };
  }

  // ── 9. Check the server ─────────────────────────────────────────────────
  // The provider may not support checkBusinessRowExists (e.g. file-storage
  // providers). If the method is absent, we cannot confirm the server's
  // state and must not proceed.
  if (!provider.checkBusinessRowExists) {
    return {
      code: 'PROVIDER_UNAVAILABLE',
      message: 'This provider does not support the business-row existence check.',
    };
  }

  let exists: { exists: boolean; totalChanges: number; cursor: string };
  try {
    const result = await provider.checkBusinessRowExists({ businessId });
    if (result === null) {
      return {
        code: 'PROVIDER_UNAVAILABLE',
        message: 'The provider cannot determine business-row existence for this business.',
      };
    }
    // Validate the shape — a malformed response must NOT be treated as
    // exists=false, which would cause an unnecessary outbox reset.
    if (
      typeof result !== 'object' ||
      result === null ||
      typeof result.exists !== 'boolean' ||
      typeof result.totalChanges !== 'number' ||
      typeof result.cursor !== 'string'
    ) {
      return {
        code: 'PROVIDER_UNAVAILABLE',
        message: 'The sync server returned an invalid business-row check response.',
      };
    }
    exists = result;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      code: 'PROVIDER_UNAVAILABLE',
      message: `Could not check server for the business row: ${message}`,
    };
  }

  // ── 10. Short-circuit: server already has the business ────────────────
  if (exists.exists) {
    return {
      outboxReset: false,
      serverHadBusiness: true,
      changeId,
      skipped: false,
    };
  }

  // ── 11. Server confirms business is missing → reset acked → pending ──
  // This is the ONLY write this function performs. We change exactly one
  // row, and only the `status` field. The changeId, payload, version,
  // deviceId, and all other fields are preserved.
  //
  // The next SyncEngine.run() will:
  //   - ensureBusinessChangeEnqueued() finds this entry (now pending) and returns
  //   - takePending() picks it up and sends it
  //   - sync_push_changes inserts it (change_id not found → new row)
  //   - sync_fetch_snapshot returns it as the business entity
  await db.syncOutbox.update(changeId, {
    status: 'pending' as const,
    retryCount: 0,
  });

  return {
    outboxReset: true,
    serverHadBusiness: false,
    changeId,
    skipped: false,
  };
}

/**
 * Verify that the outbox entry's payload matches the local business row.
 *
 * Performs a comprehensive deep comparison of ALL non-sync fields between the
 * outbox payload and the current local business row. Sync metadata fields
 * (syncVersion, syncChangeId, syncUpdatedAt) are excluded because they are
 * managed by the sync engine and may legitimately differ between the snapshot
 * time and now (e.g., syncVersion was bumped locally, syncChangeId was written
 * by applyChanges).
 *
 * This is stronger than version-only validation: a matching syncVersion does
 * not prove the business data is unchanged, and re-pushing a stale payload
 * would overwrite newer data on the cloud.
 */
function businessPayloadMatches(outboxEntry: SyncOutboxEntry, business: Business): boolean {
  const payload = outboxEntry.payload as Partial<Business> | undefined;
  if (!payload) return false;

  // Fields managed by the sync engine — excluded from comparison.
  const SYNC_PREFIX = 'sync';

  // Compare every non-sync field on the local business row.
  for (const key of Object.keys(business)) {
    if (key.startsWith(SYNC_PREFIX)) continue;
    const localVal = (business as unknown as Record<string, unknown>)[key];
    const payloadVal = (payload as unknown as Record<string, unknown>)[key];
    if (!deepEqual(localVal, payloadVal)) return false;
  }

  // Check for extra fields in the payload that are NOT on the local business row.
  // A stale outbox payload that carries fields since removed from the business
  // schema could sneak through otherwise — an extra property means the payload
  // was captured from a different version of the row.
  for (const key of Object.keys(payload)) {
    if (key.startsWith(SYNC_PREFIX)) continue;
    if (!(key in business)) return false;
  }

  return true;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => deepEqual(val, b[i]));
  }
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** Re-export for tests that need to verify the deterministic fallback. */
export const _internal = {
  deterministicBusinessChangeId,
  businessChangeIdFor,
  businessPayloadMatches,
};
